/**
 * Utilitários para sincronização de atalhos com o backend C#
 *
 * Converte atalhos do localStorage (formato KeyboardContext) para o formato
 * esperado pelo backend (shortcuts:enable/shortcuts:disable)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FLUXO RECOMENDADO (Conforme ACTIONS_FRONTEND_GUIDE.md do C#):
 * 1. Chamar shortcuts:list para verificar estado atual
 * 2. Chamar shortcuts:enable se count === 0 ou hookInstalled === false
 * 3. Verificar hookInstalled === true após enable
 * 4. Chamar shortcuts:disable ao fechar/logout
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { corelCommunication } from './communication';
import { sendToTerminal } from '../terminalLogger';

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS - Conforme contrato do backend C#
// ═══════════════════════════════════════════════════════════════════════════

export interface BackendShortcut {
  combo: string;  // Ex: "Ctrl+Shift+R"
  action: string; // Ex: "oneregua::abrir-modal"
}

export interface ShortcutsEnablePayload {
  shortcuts: BackendShortcut[];
  options?: {
    requireForeground?: boolean;
  };
}

/** Resultado de cada atalho registrado */
export interface ShortcutRegistration {
  combo: string;
  ok: boolean;
  reason?: string;
}

/** Status retornado pelo backend nas responses */
export interface ShortcutsStatus {
  enabled: boolean;
  totalRegistered?: number;
  totalFailed?: number;
  hookInstalled: boolean;
  monitorAvailable: boolean;
  requireForeground?: boolean;
}

/** Response de shortcuts:enable */
export interface ShortcutsEnableResponse {
  action: 'shortcuts:enable';
  requestId: string;
  status: 'ok' | 'error';
  result?: {
    registered: ShortcutRegistration[];
    status: ShortcutsStatus;
  };
  message?: string;
  code?: string;
}

/** Response de shortcuts:list */
export interface ShortcutsListResponse {
  action: 'shortcuts:list';
  requestId: string;
  status: 'ok' | 'error';
  result?: {
    enabled: boolean;
    count: number;
    shortcuts: BackendShortcut[];
    monitorAvailable: boolean;
    hookInstalled: boolean;
  };
  message?: string;
}

/** Response de shortcuts:disable */
export interface ShortcutsDisableResponse {
  action: 'shortcuts:disable';
  requestId: string;
  status: 'ok' | 'error';
  result?: {
    disabled: boolean;
    status: {
      enabled: boolean;
      monitorAvailable: boolean;
      hookInstalled: boolean;
    };
  };
  message?: string;
}

/** Resultado da sincronização com verificação */
export interface SyncResult {
  success: boolean;
  hookInstalled: boolean;
  totalRegistered: number;
  totalFailed: number;
  failedShortcuts: ShortcutRegistration[];
  error?: string;
}

type KeyCombo = ReadonlyArray<string>;
type ShortcutState = Record<string, Record<string, KeyCombo>>;

const STORAGE_KEY = 'cdr:shortcuts:v1';

// ═══════════════════════════════════════════════════════════════════════════
// FUNÇÕES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Converte um modificador do formato frontend para o formato backend
 * Frontend: 'Control' → Backend: 'Ctrl'
 */
function normalizeModifier(key: string): string {
  const map: Record<string, string> = {
    'Control': 'Ctrl',
    'Meta': 'Win',
  };
  return map[key] || key;
}

/**
 * Converte KeyCombo do frontend para string do backend
 * ['Control', 'Shift', 'R'] → 'Ctrl+Shift+R'
 */
export function serializeComboForBackend(keys: KeyCombo): string {
  const mods: string[] = [];
  let mainKey: string | null = null;
  
  for (const k of keys) {
    if (k === 'Control' || k === 'Shift' || k === 'Alt' || k === 'Meta') {
      mods.push(normalizeModifier(k));
    } else {
      mainKey = k;
    }
  }
  
  // Ordenar modificadores: Ctrl, Shift, Alt, Win
  const modOrder = ['Ctrl', 'Shift', 'Alt', 'Win'];
  mods.sort((a, b) => modOrder.indexOf(a) - modOrder.indexOf(b));
  
  const parts = [...mods];
  if (mainKey) parts.push(mainKey);
  
  return parts.join('+');
}

/**
 * Lê atalhos do localStorage e converte para formato do backend
 */
export function readShortcutsFromStorage(): BackendShortcut[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    
    const state: ShortcutState = JSON.parse(raw);
    if (!state || typeof state !== 'object') return [];
    
    const shortcuts: BackendShortcut[] = [];
    
    for (const [toolId, actions] of Object.entries(state)) {
      if (!actions || typeof actions !== 'object') continue;
      
      for (const [actionId, keys] of Object.entries(actions)) {
        if (!Array.isArray(keys) || keys.length === 0) continue;
        
        const combo = serializeComboForBackend(keys);
        if (!combo) continue;
        
        shortcuts.push({
          combo,
          action: `${toolId}::${actionId}`
        });
      }
    }
    
    return shortcuts;
  } catch (err) {
    sendToTerminal('⚠️ [SHORTCUTS] Erro ao ler atalhos do localStorage', {
      error: err instanceof Error ? err.message : String(err)
    });
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE COMUNICAÇÃO COM BACKEND C#
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Lista atalhos registrados no backend C# (shortcuts:list)
 *
 * Conforme ACTIONS_FRONTEND_GUIDE.md:
 * - Usar ANTES de shortcuts:enable para verificar estado atual
 * - Verificar hookInstalled para saber se atalhos funcionam
 *
 * @returns Promise com resultado da listagem
 */
export function listShortcutsFromBackend(): Promise<ShortcutsListResponse> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve({
        action: 'shortcuts:list',
        requestId: '',
        status: 'error',
        message: 'Timeout ao listar atalhos'
      });
    }, 5000);

    corelCommunication.sendActionGuarded('shortcuts:list', {}, (response) => {
      clearTimeout(timeout);
      resolve(response as ShortcutsListResponse);
    });
  });
}

/**
 * Envia atalhos para o backend C# via shortcuts:enable
 * O backend irá registrar os atalhos globais no Windows
 *
 * @deprecated Use enableShortcutsWithVerification para fluxo completo
 */
export function sendShortcutsToBackend(
  shortcuts: BackendShortcut[],
  callback?: (response: ShortcutsEnableResponse) => void
): string | null {
  if (shortcuts.length === 0) {
    sendToTerminal('ℹ️ [SHORTCUTS] Nenhum atalho para registrar');
    return null;
  }

  const payload: ShortcutsEnablePayload = {
    shortcuts,
    options: {
      requireForeground: true // Só funciona quando app está em foco
    }
  };

  sendToTerminal('🔌 [SHORTCUTS] Enviando atalhos para o backend', {
    count: shortcuts.length,
    shortcuts: shortcuts.map(s => `${s.combo} → ${s.action}`)
  });

  return corelCommunication.sendActionGuarded('shortcuts:enable', payload, (response) => {
    const typedResponse = response as ShortcutsEnableResponse;

    if (typedResponse?.status === 'ok') {
      const registered = typedResponse?.result?.registered || [];
      const status = typedResponse?.result?.status;
      const success = registered.filter((r) => r.ok).length;
      const failed = registered.filter((r) => !r.ok).length;

      sendToTerminal('✅ [SHORTCUTS] Atalhos registrados no backend', {
        success,
        failed,
        hookInstalled: status?.hookInstalled,
        enabled: status?.enabled,
        details: registered
      });
    } else {
      sendToTerminal('❌ [SHORTCUTS] Erro ao registrar atalhos', {
        status: typedResponse?.status,
        message: typedResponse?.message
      });
    }

    if (callback) callback(typedResponse);
  });
}

/**
 * Habilita atalhos com verificação de hookInstalled
 *
 * Conforme ACTIONS_FRONTEND_GUIDE.md:
 * - Verifica hookInstalled === true após enable
 * - Retorna informações detalhadas de falhas
 *
 * @param shortcuts Lista de atalhos para registrar
 * @returns Promise com resultado detalhado
 */
export function enableShortcutsWithVerification(
  shortcuts: BackendShortcut[]
): Promise<SyncResult> {
  return new Promise((resolve) => {
    if (shortcuts.length === 0) {
      sendToTerminal('ℹ️ [SHORTCUTS] Nenhum atalho para registrar');
      resolve({
        success: true,
        hookInstalled: false,
        totalRegistered: 0,
        totalFailed: 0,
        failedShortcuts: []
      });
      return;
    }

    const payload: ShortcutsEnablePayload = {
      shortcuts,
      options: {
        requireForeground: true
      }
    };

    const timeout = setTimeout(() => {
      resolve({
        success: false,
        hookInstalled: false,
        totalRegistered: 0,
        totalFailed: shortcuts.length,
        failedShortcuts: [],
        error: 'Timeout ao habilitar atalhos'
      });
    }, 10000);

    corelCommunication.sendActionGuarded('shortcuts:enable', payload, (response) => {
      clearTimeout(timeout);
      const typedResponse = response as ShortcutsEnableResponse;

      if (typedResponse?.status !== 'ok') {
        resolve({
          success: false,
          hookInstalled: false,
          totalRegistered: 0,
          totalFailed: shortcuts.length,
          failedShortcuts: [],
          error: typedResponse?.message || 'Erro desconhecido ao habilitar atalhos'
        });
        return;
      }

      const registered = typedResponse?.result?.registered || [];
      const status = typedResponse?.result?.status;
      const hookInstalled = status?.hookInstalled ?? false;
      const enabled = status?.enabled ?? false;
      const totalRegistered = status?.totalRegistered ?? registered.filter(r => r.ok).length;
      const totalFailed = status?.totalFailed ?? registered.filter(r => !r.ok).length;
      const failedShortcuts = registered.filter(r => !r.ok);

      // Verificar hookInstalled conforme documentação
      const success = hookInstalled && enabled && totalFailed === 0;

      if (success) {
        sendToTerminal('✅ [SHORTCUTS] Atalhos habilitados com sucesso', {
          totalRegistered,
          hookInstalled,
          enabled
        });
      } else {
        sendToTerminal('⚠️ [SHORTCUTS] Atalhos habilitados com problemas', {
          totalRegistered,
          totalFailed,
          hookInstalled,
          enabled,
          failedShortcuts: failedShortcuts.map(f => `${f.combo}: ${f.reason || 'unknown'}`)
        });
      }

      resolve({
        success,
        hookInstalled,
        totalRegistered,
        totalFailed,
        failedShortcuts
      });
    });
  });
}

/**
 * Desabilita todos os atalhos no backend
 */
export function disableShortcutsInBackend(callback?: (response: ShortcutsDisableResponse) => void): string | null {
  sendToTerminal('🔌 [SHORTCUTS] Desabilitando atalhos no backend');

  return corelCommunication.sendActionGuarded('shortcuts:disable', {}, (response) => {
    const typedResponse = response as ShortcutsDisableResponse;

    if (typedResponse?.status === 'ok') {
      const status = typedResponse?.result?.status;
      sendToTerminal('✅ [SHORTCUTS] Atalhos desabilitados', {
        enabled: status?.enabled,
        hookInstalled: status?.hookInstalled
      });
    } else {
      sendToTerminal('⚠️ [SHORTCUTS] Erro ao desabilitar atalhos', {
        status: typedResponse?.status,
        message: typedResponse?.message
      });
    }

    if (callback) callback(typedResponse);
  });
}

/** Resultado da desabilitação com verificação */
export interface DisableResult {
  success: boolean;
  enabled: boolean;
  hookInstalled: boolean;
  error?: string;
}

/**
 * Desabilita atalhos com verificação completa
 *
 * Conforme ACTIONS_FRONTEND_GUIDE.md:
 * - Verifica enabled === false após disable
 * - Verifica hookInstalled === false após disable
 *
 * @returns Promise com resultado detalhado
 */
export function disableShortcutsWithVerification(): Promise<DisableResult> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve({
        success: false,
        enabled: true,
        hookInstalled: true,
        error: 'Timeout ao desabilitar atalhos'
      });
    }, 5000);

    corelCommunication.sendActionGuarded('shortcuts:disable', {}, (response) => {
      clearTimeout(timeout);
      const typedResponse = response as ShortcutsDisableResponse;

      if (typedResponse?.status !== 'ok') {
        sendToTerminal('❌ [SHORTCUTS] Erro ao desabilitar atalhos', {
          status: typedResponse?.status,
          message: typedResponse?.message
        });
        resolve({
          success: false,
          enabled: true,
          hookInstalled: true,
          error: typedResponse?.message || 'Erro desconhecido ao desabilitar atalhos'
        });
        return;
      }

      const status = typedResponse?.result?.status;
      const enabled = status?.enabled ?? true;
      const hookInstalled = status?.hookInstalled ?? true;

      // Verificar que realmente desabilitou conforme documentação
      const success = enabled === false && hookInstalled === false;

      if (success) {
        sendToTerminal('✅ [SHORTCUTS] Atalhos desabilitados com sucesso', {
          enabled,
          hookInstalled
        });
      } else {
        sendToTerminal('⚠️ [SHORTCUTS] Atalhos podem não ter sido desabilitados corretamente', {
          enabled,
          hookInstalled
        });
      }

      resolve({
        success,
        enabled,
        hookInstalled
      });
    });
  });
}

/**
 * @deprecated Use syncShortcutsFromState para sincronizar a partir da API
 * Sincroniza atalhos do localStorage com o backend
 * Lê do localStorage, converte e envia para o C#
 */
export function syncShortcutsWithBackend(callback?: (response: ShortcutsEnableResponse) => void): string | null {
  const shortcuts = readShortcutsFromStorage();
  return sendShortcutsToBackend(shortcuts, callback);
}

/**
 * Converte state do KeyboardContext para lista de BackendShortcut
 */
export function stateToBackendShortcuts(state: ShortcutState): BackendShortcut[] {
  const shortcuts: BackendShortcut[] = [];

  for (const [toolId, actions] of Object.entries(state)) {
    if (!actions || typeof actions !== 'object') continue;

    for (const [actionId, keys] of Object.entries(actions)) {
      if (!Array.isArray(keys) || keys.length === 0) continue;

      const combo = serializeComboForBackend(keys);
      if (!combo) continue;

      shortcuts.push({
        combo,
        action: `${toolId}::${actionId}`
      });
    }
  }

  return shortcuts;
}

/**
 * Sincroniza atalhos de um estado externo (API) com o backend C#
 * Usado para enviar atalhos carregados da API para o C#
 *
 * @deprecated Use syncShortcutsWithVerification para fluxo completo com verificação
 */
export function syncShortcutsFromState(
  state: ShortcutState,
  callback?: (response: ShortcutsEnableResponse) => void
): string | null {
  const shortcuts = stateToBackendShortcuts(state);

  sendToTerminal('🔌 [SHORTCUTS] Sincronizando atalhos da API com backend C#', {
    count: shortcuts.length,
    tools: Object.keys(state).length
  });

  return sendShortcutsToBackend(shortcuts, callback);
}

// ═══════════════════════════════════════════════════════════════════════════
// FLUXO COMPLETO DE SINCRONIZAÇÃO (Conforme ACTIONS_FRONTEND_GUIDE.md)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sincroniza atalhos com verificação completa
 *
 * Implementa o fluxo recomendado pelo backend C#:
 * 1. Chama shortcuts:list para verificar estado atual
 * 2. Se count === 0 ou hookInstalled === false, chama shortcuts:enable
 * 3. Verifica hookInstalled === true após enable
 *
 * @param state Estado dos atalhos do KeyboardContext
 * @param options Opções de sincronização
 * @returns Promise com resultado detalhado
 */
export async function syncShortcutsWithVerification(
  state: ShortcutState,
  options?: {
    /** Se true, força re-registro mesmo se já existem atalhos */
    forceEnable?: boolean;
    /** Callback para notificar falhas ao usuário */
    onFailedShortcuts?: (failed: ShortcutRegistration[]) => void;
  }
): Promise<SyncResult> {
  const shortcuts = stateToBackendShortcuts(state);

  if (shortcuts.length === 0) {
    sendToTerminal('ℹ️ [SHORTCUTS] Nenhum atalho para sincronizar');
    return {
      success: true,
      hookInstalled: false,
      totalRegistered: 0,
      totalFailed: 0,
      failedShortcuts: []
    };
  }

  // 1. Verificar estado atual com shortcuts:list
  sendToTerminal('🔍 [SHORTCUTS] Verificando estado atual dos atalhos...');
  const listResponse = await listShortcutsFromBackend();

  if (listResponse.status !== 'ok') {
    sendToTerminal('⚠️ [SHORTCUTS] Falha ao verificar estado, tentando habilitar...', {
      message: listResponse.message
    });
    // Se falhou ao listar, tenta habilitar mesmo assim
  } else {
    const { count, hookInstalled, monitorAvailable } = listResponse.result || {};

    sendToTerminal('📋 [SHORTCUTS] Estado atual dos atalhos', {
      count,
      hookInstalled,
      monitorAvailable
    });

    // Se já tem atalhos registrados E hook instalado E não é forceEnable, não precisa fazer nada
    if (!options?.forceEnable && count && count > 0 && hookInstalled) {
      // Verificar se os atalhos são os mesmos (comparar contagem)
      if (count === shortcuts.length) {
        sendToTerminal('✅ [SHORTCUTS] Atalhos já estão sincronizados', {
          count,
          hookInstalled
        });
        return {
          success: true,
          hookInstalled: true,
          totalRegistered: count,
          totalFailed: 0,
          failedShortcuts: []
        };
      }
      // Se contagem diferente, precisa re-sincronizar
      sendToTerminal('🔄 [SHORTCUTS] Contagem diferente, re-sincronizando...', {
        backendCount: count,
        frontendCount: shortcuts.length
      });
    }
  }

  // 2. Habilitar atalhos com verificação
  sendToTerminal('🔌 [SHORTCUTS] Habilitando atalhos no backend...', {
    count: shortcuts.length
  });

  const result = await enableShortcutsWithVerification(shortcuts);

  // 3. Notificar falhas se houver callback
  if (result.totalFailed > 0 && options?.onFailedShortcuts) {
    options.onFailedShortcuts(result.failedShortcuts);
  }

  // 4. Log final
  if (result.success) {
    sendToTerminal('✅ [SHORTCUTS] Sincronização concluída com sucesso', {
      totalRegistered: result.totalRegistered,
      hookInstalled: result.hookInstalled
    });
  } else {
    sendToTerminal('❌ [SHORTCUTS] Sincronização concluída com problemas', {
      totalRegistered: result.totalRegistered,
      totalFailed: result.totalFailed,
      hookInstalled: result.hookInstalled,
      error: result.error
    });
  }

  return result;
}

/**
 * Parseia action recebida do backend no formato "toolId::actionId"
 */
export function parseShortcutAction(action: string): { toolId: string; actionId: string } | null {
  if (!action || typeof action !== 'string') return null;
  
  const parts = action.split('::');
  if (parts.length !== 2) return null;
  
  const [toolId, actionId] = parts;
  if (!toolId || !actionId) return null;
  
  return { toolId, actionId };
}

